import { Head, router, useForm, usePage } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import {
  adminSiteAdminPath,
  sendTestEmailAdminSiteAdminPath,
  sendTestNotificationAdminSiteAdminPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Props {
  email_settings: {
    emails_enabled: boolean
    env_override: boolean
  }
  clock_in_reminder_hours: number
  current_user_email: string
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Admin", href: "/admin/activity_types" },
  { title: "Site Admin", href: "/admin/site_admin" },
]

export default function Show({
  email_settings,
  clock_in_reminder_hours,
  current_user_email,
}: Props) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>()
    .props
  const [sendingTest, setSendingTest] = useState(false)
  const [sendingNotification, setSendingNotification] = useState(false)

  const { data, setData, patch, processing } = useForm({
    emails_enabled: email_settings.emails_enabled,
    clock_in_reminder_hours: clock_in_reminder_hours,
  })

  const handleSendTestEmail = () => {
    setSendingTest(true)
    router.post(
      sendTestEmailAdminSiteAdminPath(),
      {},
      {
        onFinish: () => setSendingTest(false),
      },
    )
  }

  const handleSendTestNotification = () => {
    setSendingNotification(true)
    router.post(
      sendTestNotificationAdminSiteAdminPath(),
      {},
      {
        onFinish: () => setSendingNotification(false),
      },
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(adminSiteAdminPath())
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Site Admin" />

      <div className="p-6">
        <h1 className="border-hpi-light-gray mb-6 border-b pb-2 text-2xl font-semibold">
          Site Admin
        </h1>

        {flash?.notice && (
          <div className="mb-4 rounded bg-green-100 px-4 py-2 text-green-800">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 rounded bg-red-100 px-4 py-2 text-red-800">
            {flash.alert}
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-xs">
          <section className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">Email Sending</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={data.emails_enabled}
                  onChange={(e) => setData("emails_enabled", e.target.checked)}
                  disabled={email_settings.env_override}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 disabled:opacity-50"
                />
                <span
                  className={email_settings.env_override ? "text-gray-400" : ""}
                >
                  Enable email delivery
                </span>
              </label>
              {email_settings.env_override && (
                <p className="text-sm text-amber-600">
                  Email sending is forced off by the EMAILS_ENABLED environment
                  variable. This toggle has no effect until the env var is
                  removed.
                </p>
              )}
            </div>
          </section>

          <section className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">Clock-In Reminder</h3>
            <label className="block text-sm text-gray-700">
              Send reminder after being clocked in for (hours):
              <input
                type="number"
                min={1}
                value={data.clock_in_reminder_hours}
                onChange={(e) =>
                  setData(
                    "clock_in_reminder_hours",
                    parseInt(e.target.value, 10),
                  )
                }
                className="mt-1 block w-24 rounded border border-gray-300 px-3 py-1.5 text-sm"
              />
            </label>
          </section>

          <div>
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>

        <section className="mt-8 max-w-xs border-t border-gray-200 pt-6">
          <h3 className="mb-2 text-lg font-semibold">Send Test Email</h3>
          <p className="mb-3 text-sm text-gray-600">
            Send a test email to <strong>{current_user_email}</strong> to verify
            delivery is working.
          </p>
          <button
            type="button"
            onClick={handleSendTestEmail}
            disabled={sendingTest}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
          >
            {sendingTest ? "Sending..." : "Send Test Email"}
          </button>
        </section>

        <section className="mt-8 max-w-xs border-t border-gray-200 pt-6">
          <h3 className="mb-2 text-lg font-semibold">Send Test Notification</h3>
          <p className="mb-3 text-sm text-gray-600">
            Send a test notification to yourself to verify the notification
            system is working.
          </p>
          <button
            type="button"
            onClick={handleSendTestNotification}
            disabled={sendingNotification}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
          >
            {sendingNotification ? "Sending..." : "Send Test Notification"}
          </button>
        </section>
      </div>
    </AppLayout>
  )
}
